import { useMemo, useState } from "react";
import { ConsoleShell, PageHead } from "../components/ConsoleShell.js";
import { Card } from "../components/Card.js";
import { Btn, Tag, Banner } from "../components/index.js";
import { LoadingState, ErrorState, EmptyState } from "../components/EmptyState.js";
import { Icon } from "../components/Icon.js";
import { useAsync } from "../lib/useAsync.js";
import { stores as storesApi, divisions as divisionsApi, hasRole } from "../lib/api.js";
import type { Division, Store } from "@tebiki/shared";

type Data = [Store[], Division[]];

/**
 * 店舗・事業室 — org structure management (owner/admin). A store belongs to at most one
 * 事業室 (NULL = 未所属, e.g. 本部). One-tap seed installs the standard set
 * (本部 + 10店舗 × そば/レストラン/カフェ/和食事業室) idempotently.
 */
export function StoresAdmin() {
  const canAdmin = hasRole("owner", "admin");
  const { data, loading, error, offline, reload } = useAsync<Data>(async () => {
    const [storeList, divisionList] = await Promise.all([
      storesApi.list(),
      divisionsApi.list().catch(() => [] as Division[]),
    ]);
    return [storeList, divisionList];
  }, []);
  const [storeList, divisionList] = data ?? [[], []];

  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [newStore, setNewStore] = useState("");
  const [newDivision, setNewDivision] = useState("");
  // per-row rename drafts (id → draft); absent = not editing
  const [storeDrafts, setStoreDrafts] = useState<Record<string, string>>({});
  const [divisionDrafts, setDivisionDrafts] = useState<Record<string, string>>({});
  // per-row 店舗コード drafts (PINログイン・新規登録の合流コード)
  const [codeDrafts, setCodeDrafts] = useState<Record<string, string>>({});

  const byDivision = useMemo(() => {
    const map = new Map<string, Store[]>();
    for (const s of storeList) {
      const key = s.divisionId ?? "none";
      map.set(key, [...(map.get(key) ?? []), s]);
    }
    return map;
  }, [storeList]);
  const unassigned = byDivision.get("none") ?? [];

  /** Run a mutation with busy/alert handling; true on success. */
  async function mutate(fn: () => Promise<unknown>, failMsg: string): Promise<boolean> {
    if (busy) return false;
    setBusy(true);
    setNotice(null);
    try {
      await fn();
      await reload();
      return true;
    } catch {
      alert(failMsg);
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function seed() {
    const ok = await mutate(async () => {
      const r = await storesApi.seedDefaults();
      setNotice(`初期セットを反映しました（店舗 +${r.createdStores} / 事業室 +${r.createdDivisions}。既存は重複しません）。`);
    }, "初期セットの投入に失敗しました。権限と通信環境を確認してください。");
    if (!ok) setNotice(null);
  }

  function deleteStore(s: Store) {
    if (!confirm(`店舗「${s.name}」を削除しますか？（マニュアルの店舗ラベルは未設定に見えるようになります）`)) return;
    void mutate(() => storesApi.remove(s.id), "店舗を削除できませんでした。");
  }
  function deleteDivision(d: Division) {
    if (!confirm(`事業室「${d.name}」を削除しますか？（所属店舗は未所属に戻ります）`)) return;
    void mutate(() => divisionsApi.remove(d.id), "事業室を削除できませんでした。");
  }

  const seedVisible = canAdmin && divisionList.length === 0;

  return (
    <ConsoleShell breadcrumb={["TEBIKI", "店舗・事業室"]}>
      <PageHead title={<>店舗・<span className="hl">事業室</span></>}>
        組織の構造をここで管理。店舗は1つの事業室に所属します（本部などは未所属のまま）。
      </PageHead>

      {notice ? (
        <Banner variant="info" icon={<Icon name="check" />} className="reveal">
          <span className="grow">{notice}</span>
          <button className="tag" type="button" onClick={() => setNotice(null)} aria-label="お知らせを閉じる">
            <Icon name="close" size={13} />
          </button>
        </Banner>
      ) : null}

      {loading ? (
        <LoadingState label="店舗と事業室を読み込み中…" />
      ) : error ? (
        <ErrorState message={error} offline={offline} onRetry={reload} />
      ) : (
        <>
          {seedVisible ? (
            <Card crop pad className="reveal" style={{ marginBottom: 22 }}>
              <div className="kicker">はじめに — 標準の組織構成</div>
              <p className="jp" style={{ margin: "10px 0 12px", fontSize: 14, lineHeight: 1.8, color: "var(--muted)" }}>
                本部 と 10店舗、4つの事業室（そば / レストラン / カフェ / 和食）をワンタップで用意します。
                既にある名前は重複しません。
              </p>
              <div className="row" style={{ gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
                {["本部", "信州屋 渋谷店", "信州屋 新宿店", "喜乃字屋", "La SOFFITTA", "Bistrot Gourmand", "人間関係", "BiOcafe", "山城屋庄蔵", "山城屋分店", "備中屋長左衛門"].map((n) => (
                  <Tag key={n} mono>{n}</Tag>
                ))}
              </div>
              <Btn variant="primary" lg icon={<Icon name="plus" size={16} />} onClick={() => void seed()} disabled={busy}>
                {busy ? "投入中…" : "初期セットを投入"}
              </Btn>
            </Card>
          ) : null}

          {/* ---- 事業室ごとのグループ表示 ---- */}
          <section aria-label="事業室" style={{ display: "grid", gap: 16 }}>
            {divisionList.map((d) => {
              const members = byDivision.get(d.id) ?? [];
              const draft = divisionDrafts[d.id];
              return (
                <Card key={d.id} crop pad className="reveal">
                  <div className="spread" style={{ flexWrap: "wrap", gap: 10, alignItems: "center" }}>
                    {draft !== undefined ? (
                      <span className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                        <label className="sr-only" htmlFor={`div-name-${d.id}`}>事業室名</label>
                        <input
                          id={`div-name-${d.id}`}
                          className="input jp"
                          style={{ width: 240 }}
                          value={draft}
                          onChange={(e) => setDivisionDrafts((p) => ({ ...p, [d.id]: e.target.value }))}
                          onKeyDown={(e) => {
                            if (e.key === "Escape") setDivisionDrafts(({ [d.id]: _drop, ...rest }) => rest);
                          }}
                        />
                        <Btn
                          disabled={busy || !draft.trim()}
                          onClick={() =>
                            void mutate(() => divisionsApi.update(d.id, draft.trim()), "名称を変更できませんでした。").then(
                              (ok) => ok && setDivisionDrafts(({ [d.id]: _drop, ...rest }) => rest),
                            )
                          }
                        >
                          保存
                        </Btn>
                        <Btn variant="ghost" onClick={() => setDivisionDrafts(({ [d.id]: _drop, ...rest }) => rest)}>
                          キャンセル
                        </Btn>
                      </span>
                    ) : (
                      <h2 className="display jp" style={{ fontSize: 22 }}>
                        {d.name}
                        <span className="mono" style={{ fontSize: 12, color: "var(--muted)", marginLeft: 10 }}>{members.length}店舗</span>
                      </h2>
                    )}
                    {canAdmin && draft === undefined ? (
                      <span className="row" style={{ gap: 8 }}>
                        <button
                          type="button"
                          className="tag"
                          style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}
                          onClick={() => setDivisionDrafts((p) => ({ ...p, [d.id]: d.name }))}
                        >
                          <Icon name="edit" size={13} /> 名称変更
                        </button>
                        <button
                          type="button"
                          className="tag"
                          style={{ cursor: "pointer", color: "var(--danger-ink)", display: "inline-flex", alignItems: "center", gap: 5 }}
                          onClick={() => deleteDivision(d)}
                        >
                          <Icon name="close" size={13} /> 削除
                        </button>
                      </span>
                    ) : null}
                  </div>
                  <div className="row" style={{ gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                    {members.length === 0 ? (
                      <span className="jp" style={{ fontSize: 13, color: "var(--muted)" }}>店舗がまだありません。下の一覧から割り当ててください。</span>
                    ) : (
                      members.map((s) => (
                        <span key={s.id} className="tag jp" style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 44, padding: "8px 12px" }}>
                          {s.name}
                          {canAdmin ? (
                            /* padding + negative margin: 12px glyph, ~36px hit target */
                            <button
                              type="button"
                              aria-label={`${s.name} をこの事業室から外す`}
                              onClick={() => void mutate(() => storesApi.update(s.id, { divisionId: null }), "外せませんでした。")}
                              style={{ display: "inline-flex", color: "var(--muted)", cursor: "pointer", padding: 12, margin: "-12px -10px -12px -6px" }}
                              disabled={busy}
                            >
                              <Icon name="close" size={12} />
                            </button>
                          ) : null}
                        </span>
                      ))
                    )}
                  </div>
                </Card>
              );
            })}

            {divisionList.length === 0 && !seedVisible ? (
              <EmptyState icon={<Icon name="ops" size={30} />} title="事業室はまだありません" />
            ) : null}

            {canAdmin ? (
              <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                <label className="sr-only" htmlFor="new-division">新しい事業室の名前</label>
                <input
                  id="new-division"
                  className="input jp"
                  style={{ width: 260 }}
                  placeholder="新しい事業室（例: 洋食事業室）"
                  value={newDivision}
                  onChange={(e) => setNewDivision(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newDivision.trim())
                      void mutate(() => divisionsApi.create(newDivision.trim()), "事業室を作成できませんでした。").then((ok) => ok && setNewDivision(""));
                  }}
                />
                <Btn
                  icon={<Icon name="plus" size={16} />}
                  disabled={busy || !newDivision.trim()}
                  onClick={() =>
                    void mutate(() => divisionsApi.create(newDivision.trim()), "事業室を作成できませんでした。").then((ok) => ok && setNewDivision(""))
                  }
                >
                  事業室を追加
                </Btn>
              </div>
            ) : null}
          </section>

          {/* ---- すべての店舗（名称変更・所属変更・削除） ---- */}
          <section aria-label="すべての店舗" style={{ marginTop: 30 }}>
            <div className="spread" style={{ marginBottom: 10, alignItems: "baseline" }}>
              <h2 className="display" style={{ fontSize: 22 }}>すべての店舗</h2>
              <span className="mono" style={{ fontSize: 12, color: "var(--muted)" }}>
                {storeList.length}店舗{unassigned.length ? ` · 未所属 ${unassigned.length}` : ""}
              </span>
            </div>

            {storeList.length === 0 ? (
              <EmptyState icon={<Icon name="ops" size={30} />} title="店舗はまだありません">
                {canAdmin ? "下の入力欄から追加するか、上の初期セットを投入してください。" : "管理者が店舗を追加するとここに並びます。"}
              </EmptyState>
            ) : (
              <div className="list reveal d1">
                {storeList.map((s) => {
                  const draft = storeDrafts[s.id];
                  return (
                    <div key={s.id} className="lrow" style={{ gridTemplateColumns: "minmax(0,1fr) auto auto", alignItems: "center", gap: 12 }}>
                      <div style={{ minWidth: 0 }}>
                        {draft !== undefined ? (
                          <span className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                            <label className="sr-only" htmlFor={`store-name-${s.id}`}>店舗名</label>
                            <input
                              id={`store-name-${s.id}`}
                              className="input jp"
                              style={{ width: 220 }}
                              value={draft}
                              onChange={(e) => setStoreDrafts((p) => ({ ...p, [s.id]: e.target.value }))}
                              onKeyDown={(e) => {
                                if (e.key === "Escape") setStoreDrafts(({ [s.id]: _drop, ...rest }) => rest);
                              }}
                            />
                            <Btn
                              disabled={busy || !draft.trim()}
                              onClick={() =>
                                void mutate(() => storesApi.update(s.id, { name: draft.trim() }), "名称を変更できませんでした。").then(
                                  (ok) => ok && setStoreDrafts(({ [s.id]: _drop, ...rest }) => rest),
                                )
                              }
                            >
                              保存
                            </Btn>
                            <Btn variant="ghost" onClick={() => setStoreDrafts(({ [s.id]: _drop, ...rest }) => rest)}>
                              キャンセル
                            </Btn>
                          </span>
                        ) : (
                          <span className="display jp" style={{ display: "block", fontSize: 17 }}>{s.name}</span>
                        )}
                        {/* 店舗コード — 共有iPadのPINログインと、新規ユーザー登録（viewer合流）の両方に使う */}
                        {draft === undefined ? (
                          canAdmin ? (
                            codeDrafts[s.id] !== undefined ? (
                              <span className="row" style={{ gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                                <label className="sr-only" htmlFor={`store-code-${s.id}`}>{s.name} の店舗コード</label>
                                <input
                                  id={`store-code-${s.id}`}
                                  className="input mono"
                                  style={{ width: 180 }}
                                  placeholder="例: SHINJUKU-01"
                                  value={codeDrafts[s.id]}
                                  onChange={(e) => setCodeDrafts((p) => ({ ...p, [s.id]: e.target.value }))}
                                  onKeyDown={(e) => {
                                    if (e.key === "Escape") setCodeDrafts(({ [s.id]: _d, ...rest }) => rest);
                                  }}
                                />
                                <Btn
                                  disabled={busy}
                                  onClick={() => {
                                    const v = codeDrafts[s.id].trim();
                                    void mutate(
                                      () => storesApi.update(s.id, { code: v ? v.toUpperCase() : null }),
                                      "コードを保存できませんでした（3〜24文字の英数字・ハイフンで、他店舗と重複しないこと）。",
                                    ).then((ok) => ok && setCodeDrafts(({ [s.id]: _d, ...rest }) => rest));
                                  }}
                                >
                                  保存
                                </Btn>
                                <Btn variant="ghost" onClick={() => setCodeDrafts(({ [s.id]: _d, ...rest }) => rest)}>
                                  キャンセル
                                </Btn>
                              </span>
                            ) : (
                              <button
                                type="button"
                                className="mono"
                                onClick={() => setCodeDrafts((p) => ({ ...p, [s.id]: s.code ?? "" }))}
                                title="店舗コード（共有iPadのPINログイン・新規ユーザー登録に使用）"
                                style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8, padding: "10px 8px 10px 0", border: 0, background: "none", cursor: "pointer", fontSize: 12, minHeight: 40, color: s.code ? "var(--muted)" : "var(--hi-deep)" }}
                              >
                                コード: {s.code ?? "未設定 — 設定する"} <Icon name="edit" size={11} />
                              </button>
                            )
                          ) : s.code ? (
                            <span className="mono" style={{ display: "block", marginTop: 8, fontSize: 12, color: "var(--muted)" }}>
                              コード: {s.code}
                            </span>
                          ) : null
                        ) : null}
                      </div>
                      {canAdmin ? (
                        <>
                          <span>
                            <label className="sr-only" htmlFor={`store-div-${s.id}`}>{s.name} の事業室</label>
                            <select
                              id={`store-div-${s.id}`}
                              className="select"
                              style={{ width: "auto", minWidth: 180 }}
                              value={s.divisionId ?? ""}
                              disabled={busy}
                              onChange={(e) =>
                                void mutate(
                                  () => storesApi.update(s.id, { divisionId: e.target.value || null }),
                                  "事業室を変更できませんでした。",
                                )
                              }
                            >
                              <option value="">未所属</option>
                              {divisionList.map((d) => (
                                <option key={d.id} value={d.id}>{d.name}</option>
                              ))}
                            </select>
                          </span>
                          <span className="row" style={{ gap: 6, justifyContent: "flex-end" }}>
                            {draft === undefined ? (
                              <button
                                type="button"
                                className="tag"
                                aria-label={`${s.name} の名称を変更`}
                                style={{ cursor: "pointer" }}
                                onClick={() => setStoreDrafts((p) => ({ ...p, [s.id]: s.name }))}
                              >
                                <Icon name="edit" size={14} />
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="tag"
                              aria-label={`${s.name} を削除`}
                              style={{ cursor: "pointer", color: "var(--muted)" }}
                              onClick={() => deleteStore(s)}
                              disabled={busy}
                            >
                              <Icon name="close" size={14} />
                            </button>
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="mono" style={{ fontSize: 12, color: "var(--muted)" }}>
                            {s.divisionId ? divisionList.find((d) => d.id === s.divisionId)?.name ?? "—" : "未所属"}
                          </span>
                          <span />
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {canAdmin ? (
              <div className="row" style={{ gap: 8, flexWrap: "wrap", marginTop: 14 }}>
                <label className="sr-only" htmlFor="new-store">新しい店舗の名前</label>
                <input
                  id="new-store"
                  className="input jp"
                  style={{ width: 260 }}
                  placeholder="新しい店舗（例: 信州屋 池袋店）"
                  value={newStore}
                  onChange={(e) => setNewStore(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && newStore.trim())
                      void mutate(() => storesApi.create({ name: newStore.trim() }), "店舗を作成できませんでした。").then((ok) => ok && setNewStore(""));
                  }}
                />
                <Btn
                  icon={<Icon name="plus" size={16} />}
                  disabled={busy || !newStore.trim()}
                  onClick={() =>
                    void mutate(() => storesApi.create({ name: newStore.trim() }), "店舗を作成できませんでした。").then((ok) => ok && setNewStore(""))
                  }
                >
                  店舗を追加
                </Btn>
                {!seedVisible ? (
                  <Btn variant="ghost" onClick={() => void seed()} disabled={busy} title="標準の店舗・事業室セットを追加投入（重複しません）">
                    初期セットを再投入
                  </Btn>
                ) : null}
              </div>
            ) : null}
          </section>
        </>
      )}
    </ConsoleShell>
  );
}
